import { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AuthProvider as OidcProvider,
  useAuth as useOidcAuth,
} from 'react-oidc-context';

import { setAuthToken } from '../../../../api/authToken';
import { setSessionExpiredHandler } from '../../../../api/sessionExpiry';
import { AuthContext } from '../../core/AuthContext';
import { locationPath } from '../../core/returnTo';
import { AUTH_MODES } from '../../core/authModes';

const KeycloakAuthBridge = ({ children }) => {
  const auth = useOidcAuth();
  const renewingRef = useRef(false);

  const token = auth.user?.access_token;

  useEffect(() => {
    setAuthToken(token);
  }, [token]);

  /* a 401 from the API: try a silent renew once, otherwise a fresh
     login is needed — redirect and come back to the current page */
  useEffect(() => {
    setSessionExpiredHandler(() => {
      if (renewingRef.current) return;
      renewingRef.current = true;
      auth
        .signinSilent()
        .then((user) => {
          if (!user) throw new Error('silent renew failed');
        })
        .catch(() =>
          auth.signinRedirect({
            state: { returnTo: locationPath(window.location) },
          })
        )
        .finally(() => {
          renewingRef.current = false;
        });
    });
    return () => setSessionExpiredHandler(null);
  }, [auth]);

  /* the background renewal gave up (e.g. the Keycloak session ended) */
  useEffect(
    () =>
      auth.events.addSilentRenewError(() =>
        auth.signinRedirect({
          state: { returnTo: locationPath(window.location) },
        })
      ),
    [auth]
  );

  const value = useMemo(
    () => ({
      authMode: AUTH_MODES.KEYCLOAK,
      user: auth.user?.profile,
      token: auth.user?.access_token,
      isAuthenticated: auth.isAuthenticated,
      isLoading: auth.isLoading,
      login: (args) => auth.signinRedirect(args),
      logout: () =>
        auth.signoutRedirect({
          post_logout_redirect_uri: process.env.REACT_APP_OIDC_REDIRECT_URI,
        }),
      error: auth.error,
    }),
    [auth]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const KeycloakAuthProvider = ({ children }) => {
  const navigate = useNavigate();

  /*
														      The oidcConfig takes care of silent refresh settings:
														            - automaticSilentRenew
															          - silent_redirect_uri
																        Without these settings,
																	      the library cannot renew tokens in the background,
																	            so authentication drops when the token expires.
																		          */
  const oidcConfig = {
    authority: process.env.REACT_APP_OIDC_AUTHORITY,
    client_id: process.env.REACT_APP_OIDC_CLIENT_ID,
    redirect_uri: process.env.REACT_APP_OIDC_REDIRECT_URI,
    silent_redirect_uri: `${window.location.origin}/silent-renew.html`,
    automaticSilentRenew: true,
    onSigninCallback: (user) => {
      const returnTo = user?.state?.returnTo || '/dashboard/hosting';
      navigate(returnTo, { replace: true });
    },
    monitorSession: true,
  };

  return (
    <OidcProvider {...oidcConfig}>
      <KeycloakAuthBridge>{children}</KeycloakAuthBridge>
    </OidcProvider>
  );
};
